import { useEffect, useMemo, useRef, useState } from 'react';
import { FiCamera, FiTrash2, FiUploadCloud, FiX } from 'react-icons/fi';
import Api from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';

const formatPriceInput = (value) => value.replace(/[^\d.]/g, '');

const normalizeTypeOptions = (payload) => {
  const source = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
    ? payload
    : [];

  return source.map((entry) => ({
    label: entry.value || entry.label || 'Type',
    value: entry.type_variant_id ?? entry.id ?? entry.value,
    price: Number(entry.service || 0) + Number(entry.shipping || 0),
    service: Number(entry.service || 0),
    shipping: Number(entry.shipping || 0),
  }));
};

const InquiryModal = ({ open, onClose, onSave, initialData = {} }) => {
  const [typeOptions, setTypeOptions] = useState([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [selectedType, setSelectedType] = useState(null);

  const [itemName, setItemName] = useState('');
  const [priceInput, setPriceInput] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const fileInputRef = useRef(null);
  const { t } = useLanguage();

  const priceValue = useMemo(() => {
    const parsed = Number(priceInput || 0);
    return Number.isFinite(parsed) ? parsed : 0;
  }, [priceInput]);

  const disableTypeSelection = priceValue >= 200;

  useEffect(() => {
    if (!open) return;

    setItemName(initialData.item?.text || '');
    setPriceInput(
      typeof initialData.item?.price === 'number'
        ? String(initialData.item.price)
        : initialData.item?.price || ''
    );
    setDescription(initialData.description || '');
    setImageUrl(
      typeof initialData.item?.image === 'string' ? initialData.item.image : ''
    );
    setSelectedType(
      initialData.productType?.id ||
        initialData.productType?.value ||
        initialData.productType ||
        null
    );
    setError('');
  }, [initialData, open]);

  useEffect(() => {
    if (!open) return;

    let active = true;
    setTypesLoading(true);

    Api.fetchProductTypes()
      .then((data) => {
        if (!active) return;
        const options = normalizeTypeOptions(data);
        setTypeOptions(options);
      })
      .catch(() => {
        if (active) setTypeOptions([]);
      })
      .finally(() => {
        if (active) setTypesLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open]);

  useEffect(() => {
    if (disableTypeSelection) {
      setSelectedType(null);
    }
  }, [disableTypeSelection]);

  if (!open) return null;

  const selectedTypeObject = typeOptions.find(
    (option) => option.value === selectedType
  );

  const handleFilePick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      const uploadedUrl = await Api.uploadImage(file);
      if (!uploadedUrl) {
        throw new Error(t('inquiry_modal.upload_failed'));
      }
      setImageUrl(uploadedUrl);
    } catch (uploadError) {
      setError(uploadError.message || t('inquiry_modal.upload_failed'));
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleSave = () => {
    const hasRequiredFields =
      itemName.trim() &&
      description.trim() &&
      (priceValue >= 200 || (!!selectedTypeObject && priceValue < 200));

    if (!hasRequiredFields) {
      setError(
        priceValue < 200
          ? t('inquiry_modal.fill_all_fields')
          : t('inquiry_modal.fill_required_fields')
      );
      return;
    }

    setError('');
    onSave({
      productType: selectedTypeObject
        ? {
            id: selectedTypeObject.value,
            label: selectedTypeObject.label,
            price: selectedTypeObject.price,
            service: selectedTypeObject.service,
            shipping: selectedTypeObject.shipping,
          }
        : null,
      description: description.trim(),
      item: {
        id: initialData.item?.id,
        text: itemName.trim(),
        price: Number(priceValue) || 0,
        image: imageUrl || null,
      },
    });
    onClose();
  };

  return (
    <div className="inquiry-modal">
      <div className="inquiry-modal__backdrop" onClick={onClose} />
      <div className="inquiry-modal__panel" role="dialog" aria-modal="true">
        <header className="inquiry-modal__header">
          <div>
            <p className="eyebrow">{t('inquiry_modal.eyebrow')}</p>
            <h3>
              {initialData.item
                ? t('inquiry_modal.edit_title')
                : t('inquiry_modal.add_title')}
            </h3>
          </div>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
            <FiX />
          </button>
        </header>

        <div className="inquiry-modal__body">
          <label className="inquiry-field">
            <span>{t('inquiry_modal.upload_label')}</span>
            <div className="inquiry-upload">
              {imageUrl ? (
                <>
                  <img src={imageUrl} alt="Product" />
                  <button
                    type="button"
                    className="button button--light"
                    onClick={() => setImageUrl('')}
                  >
                    {t('inquiry_modal.remove_photo')}
                  </button>
                </>
              ) : (
                <button type="button" onClick={handleFilePick}>
                  {uploading ? (
                    t('inquiry_modal.uploading')
                  ) : (
                    <>
                      <FiCamera /> {t('inquiry_modal.add_photo')}
                    </>
                  )}
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                hidden
              />
            </div>
          </label>

          <label className="inquiry-field">
            <span>{t('inquiry_modal.name_label')}</span>
            <input
              type="text"
              value={itemName}
              onChange={(event) => setItemName(event.target.value)}
              placeholder={t('inquiry_modal.name_placeholder')}
            />
          </label>

          <label className="inquiry-field">
            <span>{t('inquiry_modal.price_label')}</span>
            <input
              type="text"
              inputMode="decimal"
              value={priceInput}
              onChange={(event) => setPriceInput(formatPriceInput(event.target.value))}
              placeholder="0.00"
            />
          </label>

          <label className={`inquiry-field ${disableTypeSelection ? 'is-disabled' : ''}`}>
            <span>{t('inquiry_modal.type_label')}</span>
            <div className="inquiry-select">
              <select
                value={selectedType || ''}
                onChange={(event) => setSelectedType(event.target.value || null)}
                disabled={disableTypeSelection || typesLoading}
              >
                <option value="">
                  {typesLoading
                    ? t('inquiry_modal.type_loading')
                    : t('inquiry_modal.type_placeholder')}
                </option>
                {typeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <FiUploadCloud aria-hidden="true" />
            </div>
            {disableTypeSelection && (
              <small>{t('inquiry_modal.type_hint')}</small>
            )}
          </label>

          <label className="inquiry-field">
            <span>{t('inquiry_modal.description_label')}</span>
            <textarea
              rows={4}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder={t('inquiry_modal.description_placeholder')}
            />
          </label>

          {error && <p className="inquiry-field__error">{error}</p>}
        </div>

        <div className="inquiry-modal__footer">
          <button type="button" className="button button--ghost" onClick={onClose}>
            {t('inquiry_modal.cancel')}
          </button>
          <button type="button" className="button" onClick={handleSave}>
            {t('inquiry_modal.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InquiryModal;
